import type React from "react"
import { useState } from "react"
import { Button, Fade } from "@mui/material"
import { makeStyles } from "@mui/styles"
import { Homeless } from "types"
import { useAdminMemberGPFund } from "../hooks/useAdminMemberGPFund"


const useStyles = makeStyles(() => ({
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modal: {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    maxWidth: "460px",
    width: "100%",
    maxHeight: "90vh",
    overflow: "hidden",
    position: "relative",
  },
  modalOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    borderRadius: "12px",
    zIndex: 10,
  },
  overlayContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    fontSize: "20px",
    fontWeight: 600,
    textAlign: "center",
    padding: "0 32px",
  },
  overlayIcon: {
    fontSize: "32px",
    marginBottom: "8px",
  },
  successOverlay: {
    color: "#1FAD64",
  },
  errorOverlay: {
    color: "#E87F7F",
  },
  header: {
    padding: "24px 24px 12px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  title: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#1F2152",
    margin: 0,
  },
  closeButton: {
    fontSize: "1.25rem",
    background: "none",
    border: "none",
    color: "#7378E8",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      transform: "scale(1.25)"
    },
  },
  content: {
    padding: "0 24px 24px 24px",
  },
  description: {
    marginTop: "0px",
    marginBottom: "16px",
    color: "#5A6A81",
    fontSize: "1rem",
    fontWeight: 300,
    lineHeight: "1.7",
    "& strong": {
      fontWeight: 700,
    }
  },  
  inputGroup: {
    marginBottom: "36px",
  },
  input: {
    fontFamily: "Manrope",
    width: "100%",
    padding: "12px 16px",
    border: "1px solid #E3E3FA",
    borderRadius: "8px",
    fontSize: "1rem",
    backgroundColor: "#ffffff",
    boxSizing: "border-box",
    "&:focus": {
      outline: "none",
      borderColor: "#7378E8",
      borderWidth: "2px",
    },
    "&::placeholder": {
      color: "#A6A6BF",
    },
  },
  submitButton: {
    width: "100%",
    padding: "12px 20px",
    backgroundColor: "#7378E8",
    color: "#ffffff",
    border: "none",
    borderRadius: "1000px",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    cursor: "pointer",
    "&:hover": {
      transform: "scale(1.025)",
      backgroundColor: "#7378E8CC"
    },
    "&:disabled": {
      opacity: 0.5,
      color: "#FFFFFF",
      cursor: "not-allowed",
    },
  },
}))

type Props = {
  isOpen: boolean
  onClose: () => void
  homeless: Homeless
}

const AdminMemberGPFundDeposit: React.FC<Props> = ({ isOpen, onClose, homeless }) => {
  const classes = useStyles()
  const [amount, setAmount] = useState("")
  const { addFundsToMember, isLoading, error } = useAdminMemberGPFund()
  const [showOverlay, setShowOverlay] = useState(false)
  const [overlayType, setOverlayType] = useState<'success' | 'error' | null>(null)

  if (!isOpen) return null

  const renderOverlay = () => {
    if (!showOverlay || !overlayType) return null

    return (
      <Fade in={showOverlay}>
        <div className={classes.modalOverlay}>
          <div className={`${classes.overlayContent} ${overlayType === 'success' ? classes.successOverlay : classes.errorOverlay}`}>
            <i className={`${classes.overlayIcon} fas ${overlayType === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
            <span>{overlayType === 'success' ? 'Funds transferred successfully' : `Error: ${error || 'Failed to transfer funds'}`}</span>
          </div>
        </div>
      </Fade>
    )
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose()
    }
  }

  const handleSubmit = async () => {
    if (!amount || Number.parseFloat(amount) <= 0) {
      alert("Please enter a valid amount")
      return
    }

    await addFundsToMember(homeless.id, Number.parseFloat(amount))
    if (error) {
      setOverlayType('error')
      setShowOverlay(true)
      setTimeout(() => {
        setShowOverlay(false)
        setOverlayType(null)
      }, 4000)
      return
    } 

    setOverlayType('success')
    setShowOverlay(true)
    setTimeout(() => {
      setShowOverlay(false)
      setOverlayType(null)
      onClose()
      setAmount("")
    }, 2000)
  }

  const handleClose = () => {
    setAmount("")
    setShowOverlay(false)
    setOverlayType(null)
    onClose()
  }

  return (
    <div className={classes.overlay} onClick={handleOverlayClick} onKeyDown={handleKeyDown} tabIndex={-1}>
      <div className={classes.modal}>
        {renderOverlay()}
        <div className={classes.header}>
          <h2 className={classes.title}>Add Funds to Member Balance</h2>
          <button className={classes.closeButton} onClick={handleClose} type="button">
            <i className="far fa-times" />
          </button>
        </div>
        <div className={classes.content}>
          <p className={classes.description}>
            Transfer funds from the General Pool to{" "}
            <strong>
              {homeless.first_name ?? homeless.name} 
            </strong>
            's Balance
          </p>

          <div className={classes.inputGroup}>
            <input
              className={classes.input}
              type="number"
              step="1"
              min="1"
              placeholder="Enter Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <Button className={classes.submitButton} onClick={handleSubmit} disabled={isLoading || !amount}>
            {isLoading ? "Transferring Funds..." : "Transfer Funds"}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AdminMemberGPFundDeposit
